/**
 * LiveMarketPulse — Live commercial real estate market data widget
 *
 * Shows LoopNet market stats: avg cap rate, total listings, avg asking price, avg price/sqft.
 * Displays a graceful empty state when no data is available.
 */

import useMarketData from '../hooks/useMarketData'
import { API_STATUS } from '../services/marketDataService'
import '../styles/live-market-pulse.css'

function StatCard({ label, icon, value, sub }) {
  return (
    <div className="lmp-card">
      <span className="lmp-card-icon">{icon}</span>
      <span className="lmp-card-label">{label}</span>
      <div className="lmp-card-main">
        <span className="lmp-card-value">{value ?? '—'}</span>
      </div>
      {sub && <span className="lmp-card-date">{sub}</span>}
    </div>
  )
}

export default function LiveMarketPulse({ location = 'Chicago, IL', compact = false }) {
  const { marketStats, loading, error, lastFetchedAt, refresh } = useMarketData(location)

  // ── No LoopNet key configured ─────────────────────────────────────────────
  if (!API_STATUS.hasLoopNetKey) {
    return (
      <div className="lmp-setup-prompt">
        <div className="lmp-setup-icon">📡</div>
        <div className="lmp-setup-body">
          <strong>Connect Live Market Data</strong>
          <p>
            Add a LoopNet API key via RapidAPI to see live commercial real estate
            listings, cap rates, and market statistics.
          </p>
          <div className="lmp-setup-steps">
            <div className="lmp-setup-step">
              <span>1</span>
              <span>Go to <a href="https://rapidapi.com/hub" target="_blank" rel="noreferrer">rapidapi.com</a> and search for "LoopNet"</span>
            </div>
            <div className="lmp-setup-step">
              <span>2</span>
              <span>Subscribe (free tier available) and copy your API key</span>
            </div>
            <div className="lmp-setup-step">
              <span>3</span>
              <span>Set <code>VITE_LOOPNET_API_KEY=your_key_here</code> in your <code>.env</code> and restart</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="lmp-loading">
        <div className="lmp-spinner" />
        <span>Fetching live data from LoopNet…</span>
      </div>
    )
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="lmp-error">
        <span>⚠️ Live data unavailable: {error}</span>
        <button onClick={refresh}>Retry</button>
      </div>
    )
  }

  // ── API returned null (failed silently or returned no data) ──────────────
  if (!loading && !error && marketStats === null) {
    return (
      <div className="lmp-error">
        <span>
          ⚠️ <strong>LoopNet market data unavailable</strong> — the API did not return stats for <em>{location}</em>.
          Values shown are not live.
        </span>
        <button onClick={refresh}>Retry</button>
      </div>
    )
  }

  const isPlaceholder = !marketStats
  const stats = marketStats || {}
  const avgCap    = stats.avgCapRate      != null ? `${Number(stats.avgCapRate).toFixed(2)}%`  : '— (placeholder)'
  const totalList = stats.totalListings   != null ? stats.totalListings.toLocaleString()       : '— (placeholder)'
  const avgPrice  = stats.avgPrice        != null ? `$${Math.round(stats.avgPrice).toLocaleString()}` : '— (placeholder)'
  const priceSqft = stats.avgPricePerSqft != null ? `$${Number(stats.avgPricePerSqft).toFixed(0)}/sqft` : '— (placeholder)'

  return (
    <div className={`lmp-root ${compact ? 'lmp-compact' : ''} ${isPlaceholder ? 'lmp-placeholder' : ''}`}>
      <div className="lmp-header">
        <div className="lmp-header-left">
          <span className={`lmp-live-dot ${isPlaceholder ? 'lmp-live-dot--offline' : ''}`} />
          <span className="lmp-header-title">
            {isPlaceholder ? 'Market Pulse (Placeholder)' : 'Live Market Pulse'}
          </span>
          <span className="lmp-source">via LoopNet · {location}</span>
        </div>
        <div className="lmp-header-right">
          {lastFetchedAt && (
            <span className="lmp-updated">Updated {lastFetchedAt.toLocaleTimeString()}</span>
          )}
          <button className="lmp-refresh-btn" onClick={refresh} title="Refresh data">⟳</button>
        </div>
      </div>

      {isPlaceholder && (
        <div className="lmp-placeholder-notice">
          ⚠️ LoopNet API is not returning data — values below are not live.
        </div>
      )}

      <div className={`lmp-cards ${compact ? 'lmp-cards-compact' : ''}`}>
        <StatCard label="Avg Cap Rate"      icon="📈" value={avgCap} />
        <StatCard label="Active Listings"   icon="🏢" value={totalList} />
        <StatCard label="Avg Asking Price"  icon="💰" value={avgPrice} />
        <StatCard label="Avg Price / SqFt"  icon="📐" value={priceSqft} />
      </div>

      {!compact && (
        <div className="lmp-footer">
          <span>Source: LoopNet Commercial Real Estate</span>
          {isPlaceholder
            ? <span className="lmp-source-err">✗ LoopNet not connected</span>
            : <span className="lmp-source-ok">✓ LoopNet connected</span>
          }
        </div>
      )}
    </div>
  )
}
